// src/Public/Contact.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import uuid from "react-uuid";
import { sendEmail, addData } from "../service/APIService";
import config from "../config.json";
import "../styles/PrivacyPolicy.css";

const CATEGORIES = [
  { id: "general issue", label: "General Help", icon: "fi fi-rr-comment-alt" },
  { id: "feedback", label: "Feedback", icon: "fi fi-rr-smile" },
  { id: "app-suggestion", label: "Feature Idea", icon: "fi fi-rr-bulb" },
  { id: "expense-tracker", label: "Expense & Bills", icon: "fi fi-rr-calculator" },
  { id: "privacy", label: "Privacy / Data", icon: "fi fi-rr-shield-check" },
  { id: "bug", label: "Bug Report", icon: "fi fi-rr-bug" },
];

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("general issue");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const sessionUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const guestUser = JSON.parse(localStorage.getItem("guestUser"));
      const user = sessionUser || guestUser;
      if (user) {
        if (user.name && user.name !== "guest" && user.name !== "Guest User") {
          setName(user.name);
        }
        if (user.email && user.email !== "guest") {
          setEmail(user.email);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      setErrorMessage("Please enter a description of your issue or request.");
      return;
    }

    if (!email.trim() || !email.includes("@")) {
      setErrorMessage("Please provide a valid email address so we can reply.");
      return;
    }

    const currentDate = new Date().toISOString();
    const contactTicket = {
      id: uuid(),
      email: email.trim(),
      name: name.trim() || "Pocket App User",
      subject: category,
      description: description.trim(),
      type: "contactus",
      status: "pending",
      createddate: currentDate,
    };

    const htmlContent = `
      <p><strong>Category:</strong> ${category}</p>
      <p><strong>Description:</strong></p>
      <p>${description.trim()}</p>
      <p><strong>Contacted by:</strong> ${name.trim() || "User"} (${email.trim()})</p>
      <p><strong>Ticket ID:</strong> ${contactTicket.id}</p>
      <p><strong>Sent Date:</strong> ${currentDate}</p>
    `;

    const emailData = {
      sender: process.env.REACT_APP_SENDEREMAIL || config.supportemail,
      recipient: process.env.REACT_APP_SENDEREMAIL || config.supportemail,
      subject: `[Pocket App Support] ${category.toUpperCase()} - ${name.trim() || "User"}`,
      cc: email.trim(),
      body: htmlContent,
    };

    setLoading(true);
    setErrorMessage("");

    try {
      // 1. Record support ticket in database
      try {
        await addData(contactTicket);
      } catch (dbErr) {
        console.warn("Could not save ticket to database, continuing with email:", dbErr);
      }

      // 2. Dispatch email notification
      const emailRes = await sendEmail(emailData);
      if (emailRes && emailRes.error && !emailRes.success) {
        throw new Error(emailRes.error);
      }

      setIsSuccess(true);
      setDescription("");
    } catch (error) {
      console.error("Failed to send support email:", error);
      setErrorMessage(`Failed to send your message. Please try again or email us directly at ${config.supportemail}.`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIsSuccess(false);
    setDescription("");
    setErrorMessage("");
  };

  const appName = config.apptitle || "Pocket App - Expense tracker";

  return (
    <div className="privacy-page-container">
      {/* Hero Banner Card */}
      <div className="privacy-hero-card">
        <div className="privacy-hero-badge">
          <i className="fi fi-rr-headset"></i>
          <span>Support & Help Center</span>
        </div>
        <h1 className="privacy-hero-title">Contact Support</h1>
        <p className="privacy-hero-subtitle">
          Have an inquiry, feedback, or need help with {appName}? We're here for you.
        </p>
        <span className="privacy-hero-date">
          <i className="fi fi-rr-time-fast me-1"></i> Average response time: &lt; 24 hours
        </span>
      </div>

      {/* Quick Channels Grid */}
      <div className="privacy-trust-grid">
        <div className="privacy-trust-item text-decoration-none">
          <div className="privacy-trust-icon coral">
            <i className="fi fi-rr-envelope"></i>
          </div>
          <div className="privacy-trust-title">Direct Email</div>
          <p className="privacy-trust-desc">{config.supportemail}</p>
        </div>

        <Link to="/public/privacypolicy" className="privacy-trust-item text-decoration-none">
          <div className="privacy-trust-icon mauve">
            <i className="fi fi-rr-shield-check"></i>
          </div>
          <div className="privacy-trust-title">Privacy & Security</div>
          <p className="privacy-trust-desc">Read how your data is protected</p>
        </Link>
      </div>

      {/* Form or Success State */}
      {isSuccess ? (
        <div className="contact-success-card">
          <div className="contact-success-icon-wrap">
            <i className="fi fi-rr-check"></i>
          </div>
          <h2 className="contact-success-title">Message Sent!</h2>
          <p className="contact-success-desc">
            Thank you for reaching out. We have received your message and will follow up with you at <strong>{email}</strong> shortly.
          </p>
          <div className="privacy-btn-group">
            <button
              onClick={handleReset}
              className="privacy-btn-primary border-0"
              style={{ cursor: "pointer" }}
            >
              <i className="fi fi-rr-plus"></i> Send Another Message
            </button>
            <Link to="/" className="privacy-btn-secondary">
              <i className="fi fi-rr-home"></i> Back to Home
            </Link>
          </div>
        </div>
      ) : (
        <div className="public-contact-card">
          <div className="public-contact-card-header">
            <div className="public-contact-card-icon">
              <i className="fi fi-rr-paper-plane"></i>
            </div>
            <div>
              <h2 className="public-contact-card-title">Send a Message</h2>
              <p className="public-contact-card-subtitle">Fill in the details below and we'll reply via email</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="public-contact-form">
            {/* Name Input */}
            <div className="form-group">
              <label>
                <i className="fi fi-rr-user text-muted"></i> Your Name
              </label>
              <div className="contact-input-wrap">
                <i className="fi fi-rr-user contact-input-icon"></i>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-control"
                  placeholder="Enter your name"
                  maxLength={50}
                  required
                />
              </div>
            </div>

            {/* Email Input */}
            <div className="form-group">
              <label>
                <i className="fi fi-rr-envelope text-muted"></i> Email Address
              </label>
              <div className="contact-input-wrap">
                <i className="fi fi-rr-envelope contact-input-icon"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-control"
                  placeholder="name@example.com"
                  required
                />
              </div>
            </div>

            {/* Category Chips Selector */}
            <div className="form-group">
              <label>
                <i className="fi fi-rr-apps text-muted"></i> Topic Category
              </label>
              <div className="contact-category-chips">
                {CATEGORIES.map((cat) => {
                  const isSelected = category === cat.id;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setCategory(cat.id)}
                      className={`contact-category-chip ${isSelected ? "active" : ""}`}
                    >
                      <i className={cat.icon}></i>
                      <span>{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Message Description */}
            <div className="form-group">
              <label>
                <i className="fi fi-rr-edit text-muted"></i> How can we help?
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-control"
                placeholder="Describe your issue, feedback, or suggestion in detail..."
                rows={4}
                required
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="public-contact-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fi fi-rr-spinner fi-spin"></i> Sending message...
                </>
              ) : (
                <>
                  <i className="fi fi-rr-paper-plane"></i> Send Message
                </>
              )}
            </button>
          </form>

          {/* Error Message */}
          {errorMessage && (
            <div className="public-contact-alert error">
              <i className="fi fi-rr-exclamation"></i>
              <span>{errorMessage}</span>
            </div>
          )}
        </div>
      )}

      {/* Direct Inquiries & FAQ Card */}
      <div className="privacy-contact-card">
        <div className="privacy-contact-icon">
          <i className="fi fi-rr-interrogation"></i>
        </div>
        <h3 className="privacy-contact-title">Frequently Asked Questions</h3>
        <p className="privacy-contact-desc">
          Looking for quick answers regarding local storage, biometric sign in, or data synchronization?
        </p>
        <div className="privacy-btn-group">
          <Link to="/public/privacypolicy" className="privacy-btn-primary">
            <i className="fi fi-rr-shield-check"></i> View Privacy Policy
          </Link>
          <span className="privacy-btn-secondary">
            <i className="fi fi-rr-envelope"></i> {config.supportemail}
          </span>
        </div>
      </div>

      {/* Footer Note */}
      <div className="privacy-footer-note">
        <div>&copy; {new Date().getFullYear()} {appName}. All rights reserved.</div>
        <div className="mt-1">
          <Link to="/">Home</Link> &bull; <Link to="/public/privacypolicy">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default Contact;
